/**
 * Image uploads, client side: every image the user picks, pastes or drops
 * goes through here before it is sent. Any size is accepted; the image is
 * decoded shrunk, re-encoded (WebP, or JPEG/PNG) to fit the server's limits
 * for its purpose (packages/protocol images.ts), and stripped of all
 * metadata on the way, GPS location included (a privacy feature: the pixels
 * are redrawn onto a fresh canvas, nothing else is copied).
 *
 * The work runs in a Web Worker on OffscreenCanvas when the browser has
 * both, one image at a time, and falls back to the main thread otherwise.
 * Every job takes an AbortSignal: aborting stops the worker at once.
 */
import type { ImagePurpose } from '@neosocial/protocol';
import type { Translator } from '../../i18n';
import type { CropRect } from './crop';
import {
  encodeCrop,
  ImageError,
  optimize,
  prepare,
  type EncodedImage,
  type OptimizeResult,
  type PreparedImage,
  type Progress,
} from './pipeline';
import type { WorkerReply, WorkerRequest } from './image.worker';

export { ImageError } from './pipeline';
export type { EncodedImage, OptimizeResult, PreparedImage } from './pipeline';
export { PRESETS, presetFor, renameFor } from './plan';

export interface JobOptions {
  signal?: AbortSignal;
  onProgress?: Progress;
}

type Job = (signal: AbortSignal | undefined) => Promise<unknown>;

/* ---------------------------------------------------------- the worker */

let worker: Worker | null = null;
let workerBroken = false;
let nextId = 1;
const waiting = new Map<
  number,
  { resolve: (v: unknown) => void; reject: (e: unknown) => void; progress?: Progress }
>();

function canUseWorker(): boolean {
  if (workerBroken) return false;
  if (typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined') return false;
  if (typeof createImageBitmap === 'undefined') return false;
  try {
    return !!new OffscreenCanvas(1, 1).getContext('2d');
  } catch {
    return false;
  }
}

function getWorker(): Worker {
  if (worker) return worker;
  const w = new Worker(new URL('./image.worker.ts', import.meta.url), { type: 'module' });
  w.onmessage = (e: MessageEvent<WorkerReply>) => {
    const m = e.data;
    const job = waiting.get(m.id);
    if (!job) return;
    if ('progress' in m) {
      job.progress?.(m.progress);
      return;
    }
    waiting.delete(m.id);
    if ('ok' in m) job.resolve(m.ok);
    else
      job.reject(
        m.err.kind
          ? new ImageError(m.err.kind as ImageError['kind'], m.err.message)
          : new WorkerFailure(m.err.message),
      );
  };
  w.onerror = (e) => {
    // The worker could not load or crashed: everything waiting goes to the main thread.
    e.preventDefault();
    workerBroken = true;
    stopWorker(new WorkerFailure(e.message || 'image worker failed'));
  };
  worker = w;
  return w;
}

function stopWorker(reason: unknown): void {
  worker?.terminate();
  worker = null;
  for (const job of waiting.values()) job.reject(reason);
  waiting.clear();
}

class WorkerFailure extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WorkerFailure';
  }
}

function inWorker<T>(
  req: DistributiveOmit<WorkerRequest, 'id'>,
  o: JobOptions,
  transfer: Transferable[] = [],
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = nextId++;
    waiting.set(id, { resolve: resolve as (v: unknown) => void, reject, progress: o.onProgress });
    o.signal?.addEventListener(
      'abort',
      () => {
        if (!waiting.has(id)) return;
        // One job at a time runs in the worker, so stopping it stops only this job.
        stopWorker(abortError(o.signal));
      },
      { once: true },
    );
    getWorker().postMessage({ ...req, id } as WorkerRequest, transfer);
  });
}

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

function abortError(signal?: AbortSignal): unknown {
  return signal?.reason ?? new DOMException('Aborted', 'AbortError');
}

export function isAbort(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError';
}

/* ---------------------------------------------------------- the queue */

let tail: Promise<unknown> = Promise.resolve();

/** One image at a time: four 100 MB photos never decode at once. */
function enqueue<T>(job: Job, signal?: AbortSignal): Promise<T> {
  const run = tail.then(() => {
    if (signal?.aborted) throw abortError(signal);
    return job(signal);
  });
  tail = run.catch(() => {});
  return run as Promise<T>;
}

/** Runs `viaWorker`, or `local` on the main thread when there is no usable worker. */
async function either<T>(viaWorker: () => Promise<T>, local: () => Promise<T>): Promise<T> {
  if (canUseWorker()) {
    try {
      return await viaWorker();
    } catch (e) {
      if (!(e instanceof WorkerFailure)) throw e;
      // fall through: try once more here
    }
  }
  return local();
}

/* ---------------------------------------------------------- the API */

/**
 * Shrinks and re-encodes an image sent without the editor (chat images).
 * `uploadImageBytes` is the console's chat-image upload limit (the live
 * config's `limits.upload_image_bytes`): the result fits under it.
 */
export function optimizeImage(
  file: Blob,
  purpose: ImagePurpose,
  o: JobOptions & { uploadImageBytes?: number } = {},
): Promise<OptimizeResult> {
  const { uploadImageBytes } = o;
  return enqueue(
    () =>
      either(
        () => inWorker<OptimizeResult>({ op: 'optimize', file, purpose, uploadImageBytes }, o),
        () => optimize(file, purpose, o.onProgress, o.signal, uploadImageBytes),
      ),
    o.signal,
  );
}

/** Decodes an image for the crop editor: upright and shrunk to what the editor needs. */
export function prepareImage(
  file: Blob,
  purpose: ImagePurpose,
  o: JobOptions = {},
): Promise<PreparedImage> {
  return enqueue(
    () =>
      either(
        () => inWorker<PreparedImage>({ op: 'prepare', file, purpose }, o),
        () => prepare(file, purpose, o.onProgress),
      ),
    o.signal,
  );
}

/**
 * Encodes the editor's crop. The bitmap stays usable: the worker gets a
 * cropped copy, cut with drawImage (never createImageBitmap with a source
 * rectangle, which Chrome gets wrong for EXIF-rotated bitmaps).
 */
export function encodeCropped(
  bitmap: ImageBitmap,
  purpose: ImagePurpose,
  rect: CropRect,
  o: JobOptions = {},
): Promise<EncodedImage> {
  return enqueue(
    () =>
      either(
        async () => {
          const r = {
            sx: Math.floor(rect.sx),
            sy: Math.floor(rect.sy),
            sw: Math.max(1, Math.round(rect.sw)),
            sh: Math.max(1, Math.round(rect.sh)),
          };
          const part = cropToBitmap(bitmap, r);
          return inWorker<EncodedImage>(
            {
              op: 'encode',
              bitmap: part,
              purpose,
              rect: { sx: 0, sy: 0, sw: part.width, sh: part.height },
            },
            o,
            [part],
          );
        },
        () => encodeCrop(bitmap, purpose, rect, o.onProgress),
      ),
    o.signal,
  );
}

/** `r` of `bitmap` as a new bitmap, drawn 1:1 on an OffscreenCanvas (the worker path has one). */
export function cropToBitmap(
  bitmap: ImageBitmap,
  r: { sx: number; sy: number; sw: number; sh: number },
): ImageBitmap {
  const c = new OffscreenCanvas(r.sw, r.sh);
  const g = c.getContext('2d');
  if (!g) throw new ImageError('decode', 'no 2d context');
  g.drawImage(bitmap, r.sx, r.sy, r.sw, r.sh, 0, 0, r.sw, r.sh);
  return c.transferToImageBitmap();
}

/** Reads a blob as a data: URI (avatars and icons are sent that way). */
export function blobToDataUri(b: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(b);
  });
}

/** What to tell the user when an image cannot be used. */
export function imageErrorText(t: Translator['t'], e: unknown): string {
  const kind = e instanceof ImageError ? e.kind : 'decode';
  if (kind === 'heic') return t('img.err.heic');
  if (kind === 'unsupported') return t('img.err.unsupported');
  if (kind === 'tooLarge') return t('img.err.tooLarge');
  return t('img.err.decode');
}

/** The calm note when an animation becomes a still. */
export function stillText(
  t: Translator['t'],
  reason: NonNullable<OptimizeResult['still']>,
): string {
  if (reason === 'tooLarge') return t('img.still.tooLarge');
  if (reason === 'apng') return t('img.still.apng');
  return t('img.still.stillOnly');
}

/** True for files that may be images (some pasted or dropped files carry no type). */
export function looksLikeImage(f: File): boolean {
  if (f.type.startsWith('image/')) return true;
  return f.type === '' && /\.(png|jpe?g|gif|webp|heic|heif|avif|bmp)$/i.test(f.name);
}
